import React, { useState } from 'react';
import Loader from '../loader';
import GfModal from '../../gfcomponents/GfModal/GfModal';
import './FormVersionRevertModal.scss';
import { useApim } from '../../hooks/useApi';
import { GoFormzServices } from '../../AppSettings';
import { Form, FormAndTemplate, FormTag } from '../../types/form-types';
import { apimCall } from '../../helpers/api';
import { Checkbox } from 'semantic-ui-react';
import moment from 'moment';

export interface FormHistorFormVersionRevertModalProps {
  formId?: string;
  versionId?: string;
  onClose?: VoidFunction;
}

export default function FormVersionRevertModal(props: FormHistorFormVersionRevertModalProps) {
  const [saving, setSaving] = useState(false);
  const [formReverted, setFormReverted] = useState(false);
  const [error, setError] = useState(null);
  const [reOpenFormBeforeSaving, setReOpenFormBeforeSaving] = useState(false);
  const [formVersionResult, formVersionResultLoading] = useApim<FormAndTemplate>(
    GoFormzServices.TemplateService,
    `/formAndTemplate/${props.formId}/versions/${props.versionId}`
  );

  const [formResult, formResultLoading] = useApim<FormAndTemplate>(GoFormzServices.TemplateService, `/formAndTemplate/${props.formId}`);

  const cleanAutoTags = (form: Form) => {
    form?.tags?.forEach((x: FormTag) => {
      if (x.isAutoTag) {
        x.operation = null;
      }
    });
  };

  const getFormVersionForRevert = () => {
    if (formVersionResult?.form == null) {
      setError('This form version is unable to be reverted to.');
    }
    const formVersion = JSON.parse(JSON.stringify(formVersionResult.form)) as Form;
    cleanAutoTags(formVersion);
    formVersion.versionId = null;
    formVersion.versionNumber = null;
    formVersion.hasBeenReopened = null;
    //update the completed date to the current date
    if (formVersion.completedDate) {
      formVersion.completedDate = moment.utc().format('YYYY-MM-DDTHH:mm:ss');
    }

    formVersion.values = formVersion.values ?? [];

    //loop through the latest version values and clear any that are not in the form version we are reverting to
    (formResult.form?.values ?? []).forEach((value) => {
      const matchingValue = formVersion.values.find((x) => x.fieldId === value.fieldId);

      // If the value is not in the form version, then we want to add it as a cleared value
      if (!matchingValue) {
        formVersion.values.push({ fieldId: value.fieldId, operation: 2 });
      }
    });

    ///set all values to an update operation
    (formVersion.values ?? []).forEach((value) => {
      value.operation = 2;
    });

    return formVersion;
  };

  const onNavigateToLatest = () => {
    const url = `/forms/${props.formId}`;
    window.open(url);
    props.onClose();
  };

  const onConfirmRevert = async () => {
    // Clone the form so it can be manipulated before submitting
    // For autotags, remove the operation - the server will handle applying the autotags
    const currentForm = JSON.parse(JSON.stringify(formResult.form)) as Form;
    cleanAutoTags(currentForm);

    const formVersion = getFormVersionForRevert();
    setSaving(true);

    try {
      // If the form is completed, reopen it before saving the new version
      if (reOpenFormBeforeSaving) {
        currentForm.completedDate = null;
        await apimCall(GoFormzServices.TemplateService, `/forms/${props.formId}`, 'PUT', currentForm);
      }
    } catch (error) {
      console.error('Error occurred reopening form', error);
      setError('An error occurred while reopening the form before saving the new version');
      setSaving(false);
      return; // Don't continue if there was an error reopening the form
    }

    try {
      // Save the new form version as the one we are reverting to
      await apimCall(GoFormzServices.TemplateService, `/forms/${props.formId}`, 'PUT', formVersion);
      setSaving(false);
      setFormReverted(true);
    } catch (error) {
      console.error('Error occurred reverting form version', error);
      setError('AAn error occurred while reverting the Form version');
      setSaving(false);
    }
  };

  const loading = formVersionResultLoading || formResultLoading || saving;
  const primaryText = formReverted ? 'View Latest Version' : 'Revert to Version';
  const onPrimaryClick = formReverted ? onNavigateToLatest : onConfirmRevert;

  return (
    <GfModal
      className="form-version-revert-modal"
      title={`Revert to Version ${formVersionResult?.form?.versionNumber ?? ''} `}
      onClose={props.onClose}
      onPrimaryClick={onPrimaryClick}
      primaryText={primaryText}
      errorText={error}
      semanticUiModalProps={{ size: 'small' }}
    >
      <Loader isLoading={loading} />
      <div className="form-version-revert-modal-description">
        {formReverted && (
          <>
            <span>
              Version {formVersionResult?.form?.versionNumber} of <span className="form-name">{formVersionResult?.form?.name}</span> is now the latest version.
            </span>
          </>
        )}
        {!formReverted && (
          <>
            <span>
              Version {formVersionResult?.form?.versionNumber} of <span className="form-name">{formVersionResult?.form?.name}</span> will be recreated as the
              lastest version.
            </span>
            {formResult?.form?.completedDate != null && (
              <Checkbox
                label="Reopen form before saving version"
                checked={reOpenFormBeforeSaving}
                onChange={() => setReOpenFormBeforeSaving(!reOpenFormBeforeSaving)}
              />
            )}
          </>
        )}
      </div>
    </GfModal>
  );
}
